import { useState, useEffect } from 'react';
import { 
  CalendarCheck, 
  DollarSign, 
  TrendingUp, 
  Clock,
  MapPin,
  Car
} from 'lucide-react';
import { getDashboardStats, getZones, getVehicles } from '../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [zones, setZones] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, zonesRes, vehiclesRes] = await Promise.all([
          getDashboardStats(),
          getZones(),
          getVehicles()
        ]);
        setStats(statsRes.data);
        setZones(zonesRes.data);
        setVehicles(vehiclesRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    {
      icon: CalendarCheck,
      label: 'Total Reservations',
      value: stats?.total_reservations || 0,
      color: 'cyan'
    },
    {
      icon: Clock,
      label: 'Today\'s Reservations',
      value: stats?.today_reservations || 0,
      color: 'orange'
    },
    {
      icon: TrendingUp,
      label: 'Pending',
      value: stats?.pending_reservations || 0,
      color: 'yellow'
    },
    {
      icon: DollarSign,
      label: 'Total Revenue',
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`,
      color: 'green'
    }
  ];

  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || 'Unknown';
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle?.name || 'Unknown';
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-white lg:hidden">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div 
            key={index}
            className="admin-card p-6"
            data-testid={`stat-card-${index}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                stat.color === 'cyan' ? 'bg-cyan-500/20 text-cyan-400' :
                stat.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                stat.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-green-500/20 text-green-400'
              }`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-sm text-slate-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Zones */}
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-cyan-400" />
            Top Zones
          </h3>
          <div className="space-y-3">
            {stats?.zone_stats?.length > 0 ? (
              stats.zone_stats.map((zone, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-slate-300">{getZoneName(zone._id)}</span>
                  <span className="text-cyan-400 font-semibold">{zone.count} bookings</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Vehicles */}
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Car className="w-5 h-5 text-orange-400" />
            Most Reserved Vehicles
          </h3>
          <div className="space-y-3">
            {stats?.vehicle_stats?.length > 0 ? (
              stats.vehicle_stats.map((vehicle, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-slate-300">{getVehicleName(vehicle._id)}</span>
                  <span className="text-orange-400 font-semibold">{vehicle.count} bookings</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="admin-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Info</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-slate-400">Total Zones</p>
            <p className="text-xl font-bold text-white">{zones.length}</p>
          </div>
          <div>
            <p className="text-sm text-slate-400">Total Vehicles</p>
            <p className="text-xl font-bold text-white">{vehicles.length}</p>
          </div>
          <div>
            <p className="text-sm text-slate-400">Active Zones</p>
            <p className="text-xl font-bold text-white">{zones.filter(z => z.active).length}</p>
          </div>
          <div>
            <p className="text-sm text-slate-400">Active Vehicles</p>
            <p className="text-xl font-bold text-white">{vehicles.filter(v => v.active).length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
